import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import Constants from '../Constants';
import '../App.css';

const c_PlansHeadline = 'Transparent Pricing Built for Scale';
const c_PlansSubheadline = 'Choose the plan that fits your practice volume.';
const c_FounderBadgeText = 'FOUNDER RATE DEAL';
const c_FounderNameMarker = '(Founder)';
const c_AnnualNameMarker = 'Annual';

// Marketing copy for each plan, keyed by base plan name. Annual and founder variants share their
// base plan's list. A plan with no entry falls back to its feature rows from the database.
const c_PlanBullets = {
	'Starter': [
		'500 Training Credits / mo',
		'Spoken Voice & Text Modes',
		'Standard AI Prospect Personas',
		'Detailed Feedback via AI Coach',
	],
	'Pro': [
		'Everything in Starter',
		'1,000 Training Credits / mo',
		'Full Spoken Voice & Text Capabilities',
		'All 10 Trade & B2B Personas',
		'Unlimited Custom Sales Profiles',
		'Custom AI Prospect Persona Creation',
		'Guaranteed Rate Lock for Life',
	],
	'Elite': [
		'Everything in Pro',
		'2,000 Training Credits / mo',
		'Highest practice volume for daily reps',
	],
};

const is_founder_plan = (plan) => {
	return (typeof plan.name === 'string') && (plan.name.indexOf(c_FounderNameMarker) !== -1);
};

const get_base_plan_name = (plan) => {
	if(typeof plan.name !== 'string') {
		return '';
	}
	return plan.name
		.replace(c_FounderNameMarker, '')
		.replace(c_AnnualNameMarker, '')
		.trim();
};

const get_interval_suffix = (plan) => {
	if(plan.billing_interval === 'year') {
		return '/yr';
	}
	return '/mo';
};

const get_whole_dollars = (plan) => {
	return '$' + Math.round(plan.price_in_cents / 100);
};

const get_anchor_plan = (plan, all_plans) => {
	if(!is_founder_plan(plan)) {
		return null;
	}
	const base_name = get_base_plan_name(plan);
	const anchor = all_plans.find(function(candidate) {
		return !is_founder_plan(candidate)
			&& (candidate.billing_interval === plan.billing_interval)
			&& (get_base_plan_name(candidate) === base_name);
	});
	if(!anchor || !anchor.price_in_cents) {
		return null;
	}
	return anchor;
};

const get_cta_label = (plan) => {
	if(is_founder_plan(plan)) {
		return 'Lock ' + get_whole_dollars(plan) + get_interval_suffix(plan) + ' Rate';
	}
	return 'Select ' + plan.name;
};

const load_plans = async (set_plans, set_loading, set_error) => {
	set_loading(true);
	try {
		const url = Constants.SERVER_URL + '/api/plans';
		const response = await axios.get(url);
		const raw = response.data;
		const all_plans = Array.isArray(raw) ? raw : (raw.plans || []);
		const plans = all_plans.filter(function(p) { return p.name !== 'Beta'; });
		plans.sort(function(a, b) { return a.price_in_cents - b.price_in_cents; });
		set_plans(plans);
	} catch (error) {
		set_error('Failed to load subscription plans.');
	} finally {
		set_loading(false);
	}
};

const compute_discount_text = (plans) => {
	const monthly_plans = plans.filter(function(p) { return p.billing_interval === 'month'; });
	const annual_plans = plans.filter(function(p) { return p.billing_interval === 'year'; });
	const discounts = [];

	for(let i = 0; i < monthly_plans.length; i++) {
		const monthly = monthly_plans[i];
		const annual = annual_plans.find(function(p) { return p.name === monthly.name; });
		if(!annual || !monthly.price_in_cents || !annual.price_in_cents || (monthly.price_in_cents <= 0)) {
			continue;
		}
		
		const total_monthly_cost = monthly.price_in_cents * 12;
		const pct = 100 - ((annual.price_in_cents / total_monthly_cost) * 100);
		if(!isNaN(pct) && (pct > 0) && (pct < 100)) {
			discounts.push(pct);
		}
	}

	if(discounts.length === 0) {
		return '';
	}

	let sum = 0;
	for(let i = 0; i < discounts.length; i++) {
		sum += discounts[i];
	}
	const avg = sum / discounts.length;
	return 'Save about ' + avg.toFixed(0) + '% with annual billing';
};

const render_feature_value = (feature) => {
	if(!feature.is_enabled) {
		return 'Not included';
	}
	const limit = parseFloat(feature.limit_value);
	if(!isNaN(limit) && (limit > 1)) {
		if((feature.limit_period !== null) && (feature.limit_period !== undefined) && (feature.limit_period !== 'none')) {
			return limit + ' per ' + feature.limit_period;
		}
		return String(limit);
	}
	return 'Included';
};

const render_feature = (feature) => {
	const class_name = 'plan-feature' + (feature.is_enabled ? '' : ' plan-feature-disabled');
	return (
		<div key={feature.id} className={class_name}>
			<span className="plan-feature-name">{feature.name}</span>
			<span className="plan-feature-value">{render_feature_value(feature)}</span>
		</div>
	);
};

const render_feature_list = (features) => {
	if(!features || (features.length === 0)) {
		return null;
	}
	const sorted = features.slice().sort(function(a, b) { return a.sort_order - b.sort_order; });
	return (
		<div className="plan-features">
			{sorted.map(function(feature) { return render_feature(feature); })}
		</div>
	);
};

const render_plan_price = (plan) => {
	if(!plan.price_in_cents || !plan.billing_interval) {
		return <p className="plan-price">Price not available</p>;
	}

	return (
		<p className="plan-price">
			{get_whole_dollars(plan)}
			<span className="plan-price-interval">{get_interval_suffix(plan)}</span>
		</p>
	);
};

const render_anchor_price = (anchor_plan) => {
	if(!anchor_plan) {
		return null;
	}
	return (
		<p className="plan-anchor-price">
			{'Normally ' + get_whole_dollars(anchor_plan) + get_interval_suffix(anchor_plan)}
		</p>
	);
};

const render_bullet_list = (bullets) => {
	return (
		<ul className="plan-bullets">
			{bullets.map(function(bullet) {
				return (
					<li key={bullet} className="plan-bullet">
						<span className="plan-bullet-check">✓</span>
						{bullet}
					</li>
				);
			})}
		</ul>
	);
};

const render_plan_body = (plan) => {
	const bullets = c_PlanBullets[get_base_plan_name(plan)];
	if(bullets) {
		return render_bullet_list(bullets);
	}
	return render_feature_list(plan.features);
};

const render_plan_badge = (plan) => {
	if(!is_founder_plan(plan)) {
		return null;
	}
	return (
		<span className="plan-badge">{c_FounderBadgeText}</span>
	);
};

const render_plan_card = (plan, all_plans, on_select) => {
	const founder = is_founder_plan(plan);
	const anchor_plan = get_anchor_plan(plan, all_plans);
	const card_class = 'plan-card' + (founder ? ' plan-card-featured' : '');
	const name_class = 'plan-name' + (founder ? ' plan-name-featured' : '');
	const button_class = 'plan-select-btn' + (founder ? ' plan-select-btn-solid' : ' plan-select-btn-outline');
	return (
		<div key={plan.id} className={card_class}>
			{render_plan_badge(plan)}
			<div className="plan-card-body">
				<h5 className={name_class}>{plan.name}</h5>
				{render_plan_price(plan)}
				{render_anchor_price(anchor_plan)}
				{render_plan_body(plan)}
			</div>
			<button
				type="button"
				className={button_class}
				onClick={function() { return on_select(plan); }}>
				{get_cta_label(plan)}
			</button>
		</div>
	);
};

const render_plans_header = () => {
	return (
		<div className="plans-header">
			<h2 className="plans-headline">{c_PlansHeadline}</h2>
			<p className="plans-subheadline">{c_PlansSubheadline}</p>
		</div>
	);
};

const render_toggle = (plans, selected_interval, on_change) => {
	const discount_text = compute_discount_text(plans);
	return (
		<div className="plans-toggle-area">
			{discount_text ? <p className="plans-discount-text">{discount_text}</p> : null}
			<div className="plans-toggle-btns">
				<Button
					variant={selected_interval === 'month' ? 'primary' : 'outline-primary'}
					onClick={function() { return on_change('month'); }}>
					Monthly
				</Button>
				<Button
					variant={selected_interval === 'year' ? 'primary' : 'outline-primary'}
					onClick={function() { return on_change('year'); }}>
					Annual
				</Button>
			</div>
		</div>
	);
};

const render_plans_grid = (filtered_plans, all_plans, on_select) => {
	if(filtered_plans.length === 0) {
		return <p className="plans-empty">No plans available.</p>;
	}
	return (
		<div className="plans-grid">
			{filtered_plans.map(function(plan) { return render_plan_card(plan, all_plans, on_select); })}
		</div>
	);
};

const render_loading = () => {
	return (
		<div className="plans-loading">
			<Spinner animation="border" />
		</div>
	);
};

function SubscriptionPlansView() {
	const navigate = useNavigate();

	const [plans, set_plans] = useState([]);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	const [selected_interval, set_selected_interval] = useState('month');

	useEffect(function() {
		load_plans(set_plans, set_loading, set_error);
	}, []);

	const handle_select = (plan) => {
		localStorage.setItem(Constants.SIGNUP_SELECTED_PLAN, JSON.stringify(plan));
		navigate('/signup');
	};

	const filtered_plans = plans.filter(function(p) { return p.is_active && (p.billing_interval === selected_interval); });

	return (
		<main className="plans-main">
			{render_plans_header()}
			{loading ? render_loading() : (
				<React.Fragment>
					{error ? <Alert variant="danger">{error}</Alert> : null}
					{render_toggle(plans, selected_interval, set_selected_interval)}
					{render_plans_grid(filtered_plans, plans, handle_select)}
				</React.Fragment>
			)}
		</main>
	);
}

export default SubscriptionPlansView;
